import { permissionEngine } from '@/core/permissions/permissionEngine';
import { useAuth } from '@/core/auth/AuthContext';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
 ChevronLeft, 
 Save, 
 Plus, 
 Trash2, 
 ClipboardList, 
 Package, 
 RotateCcw,
 AlertCircle
} from 'lucide-react';
import { useRequestsEdit } from './hooks/useRequestsEdit';
import { cn } from '@/utils/cn';
import { PageHeaderNew } from '@/components/ui';

export function RequestEditPage() {
  const { profile } = useAuth();
  permissionEngine.assert(profile?.id || '', 'requests.edit');

 const navigate = useNavigate();
 const { 
 formData, 
 projects, 
 inventoryItems, 
 isLoading, 
 isSaving, 
 setFormData, 
 addItem, 
 removeItem, 
 updateRequest 
 } = useRequestsEdit();

 const [newItem, setNewItem] = useState({ inventoryItemId: '', requestedQuantity: 1, note: '' });

 const handleAddItem = () => {
 const item = inventoryItems.find(i => i.id === newItem.inventoryItemId);
 if (!item) return;
 addItem({ ...newItem, inventoryItem: item, unit: item.unit });
 setNewItem({ inventoryItemId: '', requestedQuantity: 1, note: '' });
 };

 if (isLoading) {
 return (
 <div className="mx-auto max-w-5xl animate-pulse space-y-4">
 <div className="h-16 rounded-md bg-white border border-slate-100" />
 <div className="grid gap-4 md:grid-cols-12">
 <div className="md:col-span-8 h-96 rounded-md bg-white border border-slate-100" />
 <div className="md:col-span-4 h-64 rounded-md bg-white border border-slate-100" />
 </div>
 </div>
 );
 }

 return (
 <div className="mx-auto max-w-5xl space-y-4 pb-12">
  {/* Header chuẩn Landscape */}
  <PageHeaderNew 
    title="Chỉnh sửa yêu cầu"
    subtitle="Cập nhật nội dung và danh mục vật tư."
    backUrl="/requests"
  />

  {/* Toolbar hành động */}
  <div className="flex items-center justify-end gap-2 mb-4">
    <button
      onClick={() => navigate(-1)}
      className="hidden h-10 items-center gap-2 rounded-md border border-slate-200 px-4 sm:text-sm text-xs text-slate-600 hover:bg-slate-50 transition-all active:scale-95 sm:flex"
    >
      <RotateCcw className="h-4 w-4" />
      <span>Hủy</span>
    </button>
    <button
      onClick={updateRequest}
      disabled={isSaving || !formData.title || formData.items.length === 0}
      className="flex h-10 items-center gap-2 rounded-md bg-emerald-700 px-4 sm:text-sm text-xs text-white hover:bg-emerald-800 disabled:opacity-50 transition-all active:scale-95"
    >
      <Save className="h-4 w-4" />
      <span>{isSaving ? 'Đang lưu...' : 'Lưu thay đổi'}</span>
    </button>
  </div>

 <div className="grid gap-4 md:grid-cols-12">
 {/* Main Content */}
 <div className="md:col-span-8 space-y-4">
 {/* Information Block */}
 <section className="rounded-md border border-slate-100 bg-white p-4 space-y-4">
 <div className="flex items-center gap-2 border-b border-slate-50 pb-2 text-slate-800">
 <ClipboardList className="h-4 w-4 text-emerald-600" />
 <span>Thông tin chung</span>
 </div>

 <div className="grid gap-4 sm:grid-cols-2">
 <div className="sm:col-span-2 space-y-1.5">
 <label className="sm:text-sm text-xs text-slate-500 tracking-wider">Tiêu đề yêu cầu</label>
 <input
 type="text"
 className="h-11 w-full rounded-md border border-slate-200 bg-slate-50/50 px-3 sm:text-sm text-xs outline-none focus:border-emerald-500 focus:bg-white transition-all"
 value={formData.title}
 onChange={(e) => setFormData({ ...formData, title: e.target.value })}
 />
 </div>

 <div className="space-y-1.5">
 <label className="sm:text-sm text-xs text-slate-500 tracking-wider">Dự án</label>
 <select
 className="h-11 w-full rounded-md border border-slate-200 bg-slate-50 px-3 sm:text-sm text-xs outline-none focus:border-emerald-500 transition-all"
 value={formData.projectId}
 onChange={(e) => setFormData({ ...formData, projectId: e.target.value })}
 >
 <option value="">Chọn dự án...</option>
 {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
 </select>
 </div>

 <div className="space-y-1.5">
 <label className="sm:text-sm text-xs text-slate-500 tracking-wider">Ngày cần vật tư</label>
 <input
 type="date"
 className="h-11 w-full rounded-md border border-slate-200 bg-slate-50 px-3 sm:text-sm text-xs outline-none focus:border-emerald-500 transition-all"
 value={formData.neededDate}
 onChange={(e) => setFormData({ ...formData, neededDate: e.target.value })}
 />
 </div>

 <div className="sm:col-span-2 space-y-1.5">
 <label className="sm:text-sm text-xs text-slate-500 tracking-wider">Mô tả chi tiết</label>
 <textarea
 rows={3}
 className="w-full rounded-md border border-slate-200 bg-slate-50/50 p-3 sm:text-sm text-xs outline-none focus:border-emerald-500 focus:bg-white transition-all"
 value={formData.description}
 onChange={(e) => setFormData({ ...formData, description: e.target.value })}
 />
 </div>
 </div>
 </section>

 {/* Items Block */}
 <section className="rounded-md border border-slate-100 bg-white p-4 space-y-4">
 <div className="flex items-center justify-between border-b border-slate-50 pb-2">
 <div className="flex items-center gap-2 text-slate-800">
 <Package className="h-4 w-4 text-emerald-600" />
 <span>Danh mục vật tư</span>
 </div>
 <span className="text-slate-400 tracking-widest">{formData.items.length} items</span>
 </div>

 {/* Add Item Form */}
 <div className="grid gap-3 rounded-md bg-slate-50 p-3 sm:grid-cols-12">
 <div className="sm:col-span-6 space-y-1">
 <label className="text-slate-400 tracking-wider">Vật tư</label>
 <select
 className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 sm:text-sm text-xs outline-none focus:border-emerald-500"
 value={newItem.inventoryItemId}
 onChange={(e) => setNewItem({ ...newItem, inventoryItemId: e.target.value })}
 >
 <option value="">Chọn vật tư...</option>
 {inventoryItems.map(i => <option key={i.id} value={i.id}>[{i.code}] {i.name}</option>)}
 </select>
 </div>
 <div className="sm:col-span-2 space-y-1">
 <label className="text-slate-400 tracking-wider">Số lượng</label>
 <input
 type="number"
 className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 sm:text-sm text-xs outline-none focus:border-emerald-500"
 value={newItem.requestedQuantity}
 onChange={(e) => setNewItem({ ...newItem, requestedQuantity: Number(e.target.value) })}
 />
 </div>
 <div className="sm:col-span-4 flex items-end">
 <button
 type="button"
 onClick={handleAddItem}
 disabled={!newItem.inventoryItemId}
 className="flex h-9 w-full items-center justify-center gap-2 rounded-md bg-white border border-slate-200 sm:text-sm text-xs text-emerald-600 hover:bg-emerald-50 transition-all disabled:opacity-50"
 >
 <Plus className="h-3 w-3" />
 Thêm vào list
 </button>
 </div>
 </div>

 {/* Items Table */}
 <div className="overflow-x-auto">
 <table className="w-full text-left sm:text-sm text-xs">
 <thead>
 <tr className="border-b border-slate-50 text-slate-400 tracking-tighter">
 <th className="pb-2 font-normal">Vật tư</th>
 <th className="pb-2 text-right font-normal">Số lượng</th>
 <th className="pb-2 text-right font-normal">Thao tác</th>
 </tr>
 </thead>
 <tbody className="divide-y divide-slate-50">
 {formData.items.map((item) => (
 <tr key={item.id} className="group">
 <td className="py-3">
 <p className="text-slate-700">{item.inventoryItem?.name}</p>
 <p className="text-slate-400 tracking-widest">{item.inventoryItem?.code}</p>
 </td>
 <td className="py-3 text-right text-slate-900">
 {item.requestedQuantity} {item.unit || item.inventoryItem?.unit}
 </td>
 <td className="py-3 text-right">
 <button
 type="button"
 onClick={() => removeItem(item.id)}
 className="h-7 w-7 rounded-md bg-red-50 text-red-600 hover:bg-red-100 transition-colors inline-flex items-center justify-center"
 >
 <Trash2 className="h-3 w-3" />
 </button>
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 </section>
 </div>

 {/* Sidebar */}
 <div className="md:col-span-4 space-y-4">
 <section className="rounded-md border border-slate-100 bg-white p-4 space-y-4">
 <h3 className="text-slate-400 tracking-widest border-b border-slate-50 pb-2">Cấu hình</h3>

 <div className="space-y-4">
 <div className="space-y-1.5">
 <label className="sm:text-sm text-xs text-slate-500 tracking-wider">Độ ưu tiên</label>
 <div className="grid grid-cols-2 gap-2">
 {(['normal', 'high', 'urgent'] as const).map(p => (
 <button
 key={p}
 type="button"
 onClick={() => setFormData({ ...formData, priority: p })}
 className={cn(
 'rounded-md border p-2 tracking-widest transition-all',
 formData.priority === p
 ? 'border-emerald-600 bg-emerald-50 text-emerald-600 '
 : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'
 )}
 >
 {p === 'normal' ? 'Thường' : p === 'high' ? 'Cao' : 'Khẩn'}
 </button>
 ))}
 </div>
 </div>
 </div>
 </section>

 <div className="rounded-md border border-slate-100 bg-amber-50 p-4">
 <div className="flex gap-2">
 <AlertCircle className="h-4 w-4 text-amber-600 shrink-0" />
 <p className="text-amber-600 leading-relaxed tracking-tighter">Lưu ý bảo mật</p>
 </div>
 <p className="mt-2 text-amber-600 leading-relaxed">
 Vi!c thay "i yêu cầu sau khi ã gửi có thể làm gián oạn quy trình phê duyệt hiện tại. Hãy ảm bảo thông tin chính xác.
 </p>
 </div>
 </div>
 </div>
 </div>
 );
}

